import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAuthUserRestaurant,
  useGetRestaurantCompanyLocationsQuery,
  useGetRestaurantCompanyTrackLocationsQuery,
  useGetSingleRestaurantCompanyQuery,
} from '@durma-soft/gros-sdk';

import { Link } from '@/components/shared/link';
import { DataTable } from '@/components/shared/data-table';
import { buttonVariants } from '@/components/shared/shadcn-ui/button';
import { CompanyBreadcrumb } from '@/components/restaurant/companies/company-breadcrumb';

import { columns as locationColumns } from '@/config/restaurant/company-locations';
import { columns as trackLocationColumns } from '@/config/restaurant/company-track-locations';

const RestaurantCompany = () => {
  const { t } = useTranslation();
  const { companyId } = useParams();

  if (!companyId) {
    throw new Error('Company ID is required for the company page');
  }

  const restaurant = useAuthUserRestaurant();

  const [memoizedLocationColumns, memoizedTrackLocationColumns] =
    useMemo(() => {
      return [locationColumns(t), trackLocationColumns(t)];
    }, [t]);

  const { data: company } = useGetSingleRestaurantCompanyQuery({
    restaurant_id: restaurant.id,
    company_id: Number(companyId),
  });

  const locations = useGetRestaurantCompanyLocationsQuery({
    restaurant_id: restaurant.id,
    company_id: Number(companyId),
  });

  const trackLocations = useGetRestaurantCompanyTrackLocationsQuery({
    restaurant_id: restaurant.id,
    company_id: Number(companyId),
  });

  return (
    <>
      <Helmet>
        <title>{company?.name ?? t('company.title')}</title>
      </Helmet>
      <CompanyBreadcrumb companyId={Number(companyId)} />
      <div>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-lg font-semibold md:text-2xl">
            {company?.name ?? '…'}
          </h1>
          <Link
            to={`/restaurant/companies/${companyId}/employees`}
            className={buttonVariants({ variant: 'outline' })}
          >
            {t('company.employees')}
          </Link>
        </div>
        <div className="flex flex-col gap-8 mt-4">
          {/* Company locations */}
          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-semibold md:text-lg">
                {t('company-locations.title')}
              </h2>
              <Link
                to={`/restaurant/companies/${companyId}/locations/create`}
                className={buttonVariants({ variant: 'default' })}
              >
                {t('company-locations.create')}
              </Link>
            </div>
            <DataTable
              columns={memoizedLocationColumns}
              data={locations.data}
              isLoading={locations.isLoading}
            />
          </section>
          {/* Company track locations */}
          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-semibold md:text-lg">
                {t('company.track-location.title')}
              </h2>
              <Link
                to={`/restaurant/companies/${companyId}/track-locations/create`}
                className={buttonVariants({ variant: 'default' })}
              >
                {t('company.track-location.create')}
              </Link>
            </div>
            <DataTable
              columns={memoizedTrackLocationColumns}
              data={trackLocations.data}
              isLoading={trackLocations.isLoading}
            />
          </section>
        </div>
      </div>
    </>
  );
};

export default RestaurantCompany;
